import { Button } from "@/components/_Shared/Button";
import { SliderWithInput } from "@/components/_Shared/Form/Inputs/Slider";
import { Control, UseFormRegister, FieldErrors } from "react-hook-form";
import { z } from "zod";
import { InvoiceItemSchema } from "./InvoiceFormContainer";

type InvoiceFormValues = {
    invoices: z.infer<typeof InvoiceItemSchema>[];
};

type InvoiceFormProps = {
    index: number;
    onRemoveInvoice: () => void;
    control: Control<InvoiceFormValues>;
    register: UseFormRegister<InvoiceFormValues>;
    errors: FieldErrors<InvoiceFormValues>;
};

/**
 * Individual invoice form component that allows users to input billing amounts and notes
 * 
 * @component
 * @param {number} index - The index of this invoice in the form array
 * @param {() => void} onRemoveInvoice - Callback function to remove this invoice
 * @param {Control<InvoiceFormValues>} control - React Hook Form control object
 * @param {UseFormRegister<InvoiceFormValues>} register - React Hook Form register function
 * @param {FieldErrors<InvoiceFormValues>} errors - Form validation errors
 * 
 * @example
 * ```tsx
 * <InvoiceForm
 *   index={0}
 *   onRemoveInvoice={() => removeInvoice(0)}
 *   control={control}
 *   register={register}
 *   errors={errors}
 * />
 * ```
 */
export function InvoiceForm({ index, onRemoveInvoice, control, register, errors }: InvoiceFormProps) {
    return (
        <div className="flex flex-row gap-2">
            <div className="w-full p-4 my-2 border rounded-lg shadow-lg bg-orange-200/60 border-orange-500/60">
                <div className="mb-4">
                    <SliderWithInput<InvoiceFormValues>
                        label="Invoice Billing Amount (%)" 
                        defaultValue={0} 
                        name={`invoices.${index}.payment_amount`} 
                        required={true} 
                        max={100} 
                        min={0} 
                        step={1} 
                        control={control}
                    />
                    {errors.invoices?.[index]?.payment_amount && (
                        <p className="text-red-500">{errors.invoices[index].payment_amount.message}</p>
                    )}
                </div>
                
                <div className="flex flex-col gap-2">
                    <label htmlFor={`invoices.${index}.notes`}>Notes</label>
                    <textarea 
                        id={`invoices.${index}.notes`}
                        {...register(`invoices.${index}.notes`)}
                        className="p-2 bg-white border border-gray-400 rounded-xl focus:outline-none focus:ring focus:ring-orange-400 dark:bg-stone-900 dark:border-stone-700 dark:text-stone-100 dark:focus:ring-orange-700"
                    />
                    {errors.invoices?.[index]?.notes && (
                        <p className="text-red-500">{errors.invoices[index].notes.message}</p>
                    )}
                </div>
            </div>

            <div className="flex justify-center gap-2">
                <Button onClick={onRemoveInvoice}>Remove Invoice</Button>
            </div>
        </div>
    )
}